import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { Markdown } from "../markdown/Markdown.js";
import {
  clickPlainOffset,
  insertBlock,
  joinDoc,
  mergeBack,
  moveBlock,
  plainToRawOffset,
  removeBlock,
  replaceBlock,
  splitBlock,
  splitDoc,
  type BlockDoc,
} from "./blockDoc.js";
import { filesFrom, uploadAll } from "./assets.js";
import { useAssetDrop } from "./useAssetDrop.js";

/**
 * Markdown mode: per-block raw editing, with no editor library and no
 * contentEditable anywhere.
 *
 * Click a block, get that block's raw markdown in a textarea; leave it, and it
 * re-renders. The document is a list of block strings plus the exact text
 * between them (blockDoc.ts), so every edit — including split, merge, move and
 * delete — rewrites only the bytes it touched.
 *
 * What this buys over the rich mode:
 *   - **nothing is un-editable.** No whitelist and no lossless round-trip to
 *     satisfy, because it never converts a DOM back into markdown. Tables,
 *     raw HTML, footnotes, `:::sidebyside` rows and nested lists all just work.
 *   - **an edit is a local splice.** Opening a page and typing one character
 *     changes one character, which the rich mode cannot promise.
 *   - **the caret lands where you clicked** (plainToRawOffset), not at the
 *     block start — the refinement left open in docs/wiki/EditModes.md.
 *   - **blocks you aren't editing stay fully rendered**, embeds live and
 *     editable through their normal modal.
 *
 * What it gives up is what a document model gives you: no cross-block
 * selection, no single undo history, and markdown in your face while typing.
 * That is exactly the trade the mode switch exists to let you make per session.
 */
export function BlockEditor({
  draft,
  setDraft,
  path,
  repo,
}: {
  draft: string;
  setDraft: (next: string) => void;
  path: string;
  repo: string;
}) {
  const [doc, setDoc] = useState<BlockDoc>(() => splitDoc(draft));
  const [editing, setEditing] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  // Caret to apply once the textarea for `editing` mounts.
  const caretRef = useRef<number | null>(null);
  const lastPushed = useRef(draft);

  // Re-split from the draft only when the change came from outside this
  // component (a mode flip, a reload, a background refetch) — never while we
  // are the ones typing, which would fight the caret.
  useEffect(() => {
    if (draft !== lastPushed.current) {
      setDoc(splitDoc(draft));
      lastPushed.current = draft;
    }
  }, [draft]);

  const push = (next: BlockDoc): void => {
    setDoc(next);
    const text = joinDoc(next);
    lastPushed.current = text;
    setDraft(text);
  };

  // An upload resolves long after the drop, so the placeholder swap has to see
  // the doc as it is *then*, not as it was at drop time. Refs rather than a
  // state updater: `push` notifies the page, and a state updater must stay
  // pure — React invokes it twice under StrictMode.
  const docRef = useRef(doc);
  docRef.current = doc;
  const editingRef = useRef(editing);
  editingRef.current = editing;

  // Drops onto the *active textarea* insert at the caret, with an optimistic
  // placeholder — you are already rewriting that block, so there's nothing to
  // preserve. Drops onto a rendered block insert a new block instead (below).
  const setEditingBlockValue = (updater: (prev: string) => string): void => {
    const i = editingRef.current;
    if (i === null) return;
    const cur = docRef.current;
    push(replaceBlock(cur, i, updater(cur.blocks[i])));
  };
  const textareaDrop = useAssetDrop(taRef, setEditingBlockValue, repo, path);

  useLayoutEffect(() => {
    const ta = taRef.current;
    if (editing === null || !ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
    if (caretRef.current !== null) {
      const pos = Math.min(caretRef.current, ta.value.length);
      ta.setSelectionRange(pos, pos);
      caretRef.current = null;
    }
    ta.focus();
  }, [editing, doc]);

  const onBlockClick = (i: number, e: MouseEvent<HTMLDivElement>): void => {
    // Let embeds keep their own click/double-click handling.
    if ((e.target as HTMLElement).closest(".dw-embed-frame")) return;
    // …and let links inside prose behave like links.
    if ((e.target as HTMLElement).closest("a")) return;
    const plain = clickPlainOffset(e.currentTarget, e.clientX, e.clientY);
    caretRef.current = plainToRawOffset(doc.blocks[i], plain);
    setEditing(i);
  };

  const onKeyDown = (i: number, e: KeyboardEvent<HTMLTextAreaElement>): void => {
    const ta = e.currentTarget;
    const { selectionStart: s, selectionEnd: en, value } = ta;

    if (e.key === "Escape") {
      e.preventDefault();
      setEditing(null);
      return;
    }
    // Enter splits into a new block; Shift+Enter is a plain newline inside it.
    if (e.key === "Enter" && !e.shiftKey && !e.metaKey) {
      e.preventDefault();
      push(splitBlock(replaceBlock(doc, i, value), i, s));
      caretRef.current = 0;
      setEditing(i + 1);
      return;
    }
    // Backspace at the very start merges into the previous block.
    if (e.key === "Backspace" && s === 0 && en === 0) {
      if (i === 0) return;
      e.preventDefault();
      const { doc: merged, offset } = mergeBack(replaceBlock(doc, i, value), i);
      caretRef.current = offset;
      push(merged);
      setEditing(i - 1);
      return;
    }
    // Up/Down at the edges walk to the neighbouring block, so the document
    // reads as one document rather than a grid of textareas.
    if (e.key === "ArrowUp" && s === 0 && i > 0) {
      e.preventDefault();
      push(replaceBlock(doc, i, value));
      caretRef.current = doc.blocks[i - 1].length;
      setEditing(i - 1);
      return;
    }
    if (e.key === "ArrowDown" && s === value.length && i < doc.blocks.length - 1) {
      e.preventDefault();
      push(replaceBlock(doc, i, value));
      caretRef.current = 0;
      setEditing(i + 1);
    }
  };

  /**
   * Drop onto a rendered block: upload, then insert the markdown as a **new
   * block** at the drop point. Inserting a block rather than splicing into
   * whatever block was under the pointer keeps the contract intact — no
   * existing block's bytes are touched.
   */
  const ingestAt = (index: number, files: File[]): void => {
    setError(null);
    void uploadAll(files, repo, path, setError).then((md) => {
      // docRef, not `doc`: the upload resolved after this closure was made.
      if (md) push(insertBlock(docRef.current, index, md));
    });
  };

  const dropProps = (index: number) => ({
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => setDragging(false),
    onDrop: (e: DragEvent) => {
      const dropped = filesFrom(e.dataTransfer);
      if (!dropped.length) return;
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      ingestAt(index, dropped);
    },
  });

  return (
    <>
      {error && <p className="dw-error">{error}</p>}
      <div
        className={`dw-blocks${dragging ? " dw-dropping" : ""}`}
        {...dropProps(doc.blocks.length)}
      >
        {doc.blocks.map((block, i) =>
          editing === i ? (
            <div className="dw-block dw-block-editing" key={i} data-block={i}>
              <textarea
                ref={taRef}
                data-testid={`dw-block-input-${i}`}
                className="dw-block-input"
                value={block}
                spellCheck={false}
                onChange={(e) => {
                  push(replaceBlock(doc, i, e.target.value));
                  const ta = e.currentTarget;
                  ta.style.height = "auto";
                  ta.style.height = `${ta.scrollHeight}px`;
                }}
                onKeyDown={(e) => onKeyDown(i, e)}
                onBlur={() => setEditing((cur) => (cur === i ? null : cur))}
                onPaste={textareaDrop.onPaste}
                onDrop={textareaDrop.onDrop}
                onDragOver={textareaDrop.onDragOver}
              />
            </div>
          ) : (
            <div
              className="dw-block"
              key={i}
              data-block={i}
              data-testid={`dw-block-${i}`}
              onClick={(e) => onBlockClick(i, e)}
              {...dropProps(i + 1)}
            >
              <div className="dw-block-gutter" onClick={(e) => e.stopPropagation()}>
                <button
                  title="Insert a block below"
                  onClick={() => {
                    push(insertBlock(doc, i + 1, ""));
                    caretRef.current = 0;
                    setEditing(i + 1);
                  }}
                >
                  +
                </button>
                <button title="Move up" onClick={() => push(moveBlock(doc, i, -1))}>
                  ↑
                </button>
                <button title="Move down" onClick={() => push(moveBlock(doc, i, 1))}>
                  ↓
                </button>
                <button title="Delete this block" onClick={() => push(removeBlock(doc, i))}>
                  ×
                </button>
              </div>
              {block.trim() === "" ? (
                <p className="dw-muted dw-empty-block">empty block — click to type</p>
              ) : (
                <Markdown
                  source={block}
                  path={path}
                  repo={repo}
                  onSourceChange={(next) => push(replaceBlock(doc, i, next))}
                />
              )}
            </div>
          ),
        )}
      </div>
    </>
  );
}
